// Produce local (centered at origin) outline points for a plate shape.
export function getLocalPoints(plate, circleSegments = 64) {
  const t = plate.type;
  const p = plate.params || {};
  const pts = [];
  if (t === "custom") {
    return (p.points || []).map((pt) => ({ x: pt.x, y: pt.y }));
  } else if (t === "rectangle") {
    const w = (p.w || 40) / 2;
    const h = (p.h || 30) / 2;
    pts.push({ x: -w, y: -h }, { x: w, y: -h }, { x: w, y: h }, { x: -w, y: h });
  } else if (t === "circle") {
    const r = p.r || 20;
    for (let i = 0; i < circleSegments; i++) {
      const a = (i / circleSegments) * Math.PI * 2;
      pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
  } else if (t === "triangle") {
    const r = p.r || 24;
    for (let i = 0; i < 3; i++) {
      const a = Math.PI / 2 + (i / 3) * Math.PI * 2;
      pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
  } else if (t === "star") {
    const n = p.points || 5;
    const oR = p.outerR || 24;
    const iR = p.innerR || 11;
    for (let i = 0; i < n * 2; i++) {
      const a = Math.PI / 2 + (i / (n * 2)) * Math.PI * 2;
      const r = i % 2 === 0 ? oR : iR;
      pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
  } else if (t === "polygon") {
    const n = Math.max(3, p.sides || 6);
    const r = p.r || 22;
    for (let i = 0; i < n; i++) {
      const a = Math.PI / 2 + (i / n) * Math.PI * 2;
      pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
  }
  return pts;
}

// Transform local points to world (apply rotation deg + translate).
export function getWorldPoints(plate) {
  const rad = ((plate.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return getLocalPoints(plate).map((pt) => ({
    x: plate.x + pt.x * cos - pt.y * sin,
    y: plate.y + pt.x * sin + pt.y * cos,
  }));
}

export function pointInPolygon(px, py, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    const intersect =
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// Nearest point on the polygon boundary, moved `inset` mm inward. Returns {x,y,dist}.
export function nearestInsetPoint(point, poly, inset) {
  let best = null;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const abx = b.x - a.x, aby = b.y - a.y;
    const len2 = abx * abx + aby * aby || 1;
    let t = ((point.x - a.x) * abx + (point.y - a.y) * aby) / len2;
    t = Math.max(0, Math.min(1, t));
    const qx = a.x + abx * t, qy = a.y + aby * t;
    const d = Math.hypot(point.x - qx, point.y - qy);
    if (!best || d < best.dist) {
      let nx = -aby, ny = abx;
      const nl = Math.hypot(nx, ny) || 1;
      nx /= nl; ny /= nl;
      if (!pointInPolygon(qx + nx * 0.01, qy + ny * 0.01, poly)) { nx = -nx; ny = -ny; }
      best = { x: qx + nx * inset, y: qy + ny * inset, dist: d };
    }
  }
  return best;
}
